import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { PayrollEntry, Employee, CompanySettings } from '../types';
import type { TaxSummary } from './taxReportGenerator';
import { parseLocalDateForDisplay } from './payrollPeriodCalculator';

interface ReportTotals {
  grossPay: number;
  netPay: number;
  taxDeductions: number;
  federalTax: number;
  medicareTax: number;
  socialSecurityTax: number;
  stateTax: number;
  insuranceDeductions: number;
  retirementDeductions: number;
}

const formatCurrency = (amount: number) => {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const generateReportPDF = async (
  reportType: 'payroll' | 'tax' | 'deductions',
  reportData: PayrollEntry[],
  employees: Employee[],
  periodLabel: string,
  getFormattedPeriod: (entry: PayrollEntry) => string,
  companySettings?: CompanySettings | null
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;

  // Add company header
  let startY = 15;
  if (companySettings) {
    if (companySettings.displayLogo && companySettings.logoUrl) {
      try {
        const img = new Image();
        img.src = companySettings.logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        doc.addImage(img, 'PNG', pageWidth / 2 - 15, startY, 30, 30, undefined, 'FAST');
        startY += 35;
      } catch (error) {
        console.error('Error loading logo:', error);
        doc.setFontSize(16);
        doc.text(companySettings.companyName, pageWidth / 2, startY, { align: 'center' });
        startY += 8;
      }
    } else {
      doc.setFontSize(16);
      doc.text(companySettings.companyName, pageWidth / 2, startY, { align: 'center' });
      startY += 8;
    }

    doc.setFontSize(8);
    doc.text(companySettings.companyAddress, pageWidth / 2, startY, { align: 'center' });
    startY += 4;
    doc.text(companySettings.companyPhone, pageWidth / 2, startY, { align: 'center' });
    startY += 6;
  }

  // Add report header
  doc.setFontSize(14);
  doc.text(
    reportType.charAt(0).toUpperCase() + reportType.slice(1) + ' Report',
    pageWidth / 2,
    startY,
    { align: 'center' }
  );

  doc.setFontSize(12);
  doc.text(periodLabel, pageWidth / 2, startY + 6, { align: 'center' });

  startY += 15;

  // Calculate totals
  const totals = reportData.reduce((acc, entry) => ({
    grossPay: acc.grossPay + entry.grossPay,
    netPay: acc.netPay + entry.netPay,
    taxDeductions: acc.taxDeductions + entry.deductions.tax,
    federalTax: acc.federalTax + entry.deductions.federalTax,
    medicareTax: acc.medicareTax + entry.deductions.medicareTax,
    socialSecurityTax: acc.socialSecurityTax + entry.deductions.socialSecurityTax,
    stateTax: acc.stateTax + entry.deductions.stateTax,
    insuranceDeductions: acc.insuranceDeductions + entry.deductions.insurance,
    retirementDeductions: acc.retirementDeductions + entry.deductions.retirement
  }), {
    grossPay: 0,
    netPay: 0,
    taxDeductions: 0,
    federalTax: 0,
    medicareTax: 0,
    socialSecurityTax: 0,
    stateTax: 0,
    insuranceDeductions: 0,
    retirementDeductions: 0
  } as ReportTotals);

  const summaryData = [];
  let skipDetailedRecords = false;

  switch (reportType) {
    case 'payroll':
      summaryData.push(
        ['Total Gross Pay', formatCurrency(totals.grossPay)],
        ['Total Net Pay', formatCurrency(totals.netPay)],
        ['Total Deductions', formatCurrency(totals.grossPay - totals.netPay)]
      );
      break;
    case 'tax':
      // Group entries by employee for individual summaries
      const employeeSummaries = reportData.reduce((acc, entry) => {
        const employee = employees.find(e => e.id === entry.employeeId);
        if (!employee) return acc;
        
        if (!acc[employee.id]) {
          acc[employee.id] = {
            name: `${employee.firstName} ${employee.lastName}`,
            federalTax: 0,
            medicareTax: 0,
            socialSecurityTax: 0,
            stateTax: 0,
            grossPay: 0
          };
        }
        
        acc[employee.id].federalTax += entry.deductions.federalTax;
        acc[employee.id].medicareTax += entry.deductions.medicareTax;
        acc[employee.id].socialSecurityTax += entry.deductions.socialSecurityTax;
        acc[employee.id].stateTax += entry.deductions.stateTax;
        acc[employee.id].grossPay += entry.grossPay;
        
        return acc;
      }, {} as Record<string, TaxSummary>);

      // Calculate totals
      const totalFederalTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.federalTax, 0);
      const totalMedicareTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.medicareTax, 0);
      const totalSocialSecurityTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.socialSecurityTax, 0);
      const totalStateTax = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.stateTax, 0);
      const totalGrossPay = Object.values(employeeSummaries).reduce((sum, emp) => sum + emp.grossPay, 0);

      // 1. Individual Employee Summaries
      doc.setFontSize(11);
      doc.text('Individual Employee Tax Summaries', margin, startY);
      startY += 5;

      autoTable(doc, {
        startY,
        head: [['Employee', 'Gross Pay', 'State Tax', 'Federal Tax', 'Social Security Tax', 'Medicare Tax', 'Total Tax']],
        body: Object.values(employeeSummaries).map(summary => {
          const totalTax = summary.stateTax + summary.federalTax + summary.socialSecurityTax + summary.medicareTax;
          return [
            summary.name,
            formatCurrency(summary.grossPay),
            formatCurrency(summary.stateTax),
            formatCurrency(summary.federalTax),
            formatCurrency(summary.socialSecurityTax),
            formatCurrency(summary.medicareTax),
            formatCurrency(totalTax)
          ];
        }),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1 },
        headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
        margin
      });

      // 2. Employer Tax Liability
      startY = (doc as any).lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.text('Employer Tax Liability Summary', margin, startY);
      startY += 5;

      autoTable(doc, {
        startY,
        head: [['Category', 'Amount']],
        body: [
          ['Employer Social Security Tax', formatCurrency(totalSocialSecurityTax)],
          ['Employer Medicare Tax', formatCurrency(totalMedicareTax)],
          ['Total Employer Tax Liability', formatCurrency(totalSocialSecurityTax + totalMedicareTax)]
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1 },
        headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
        margin
      });

      // 3. Consolidated Tax Summary
      startY = (doc as any).lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.text('Consolidated Tax Summary', margin, startY);
      startY += 5;

      autoTable(doc, {
        startY,
        head: [['Category', 'Amount']],
        body: [
          ['Total Gross Pay', formatCurrency(totalGrossPay)],
          ['Total State Tax Due', formatCurrency(totalStateTax)],
          ['Total Federal Income Tax Due', formatCurrency(totalFederalTax)],
          ['Total Social Security Tax (Combined)', formatCurrency(totalSocialSecurityTax * 2)],
          ['Total Medicare Tax (Combined)', formatCurrency(totalMedicareTax * 2)],
          ['Grand Total Tax Due', formatCurrency(
            totalStateTax +
            totalFederalTax +
            (totalSocialSecurityTax * 2) +
            (totalMedicareTax * 2)
          )]
        ],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1 },
        headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
        margin,
        didDrawCell: (data) => {
          // Highlight total row
          if (data.row.index === 5) {
            data.cell.styles.fillColor = [243, 244, 246];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      // Save and return early since we've handled the tax report
      doc.save(`tax-report-${new Date().toISOString().split('T')[0]}.pdf`);
      return;
      break;
    case 'deductions':
      summaryData.push(
        ['Total Tax Deductions', formatCurrency(totals.taxDeductions)],
        ['Total Insurance Deductions', formatCurrency(totals.insuranceDeductions)],
        ['Total Retirement Deductions', formatCurrency(totals.retirementDeductions)],
        ['Total Deductions', formatCurrency(
          totals.taxDeductions + 
          totals.insuranceDeductions + 
          totals.retirementDeductions
        )]
      );
      break;
  }

  // Add summary table
  if (!skipDetailedRecords) {
    autoTable(doc, {
      startY,
      head: [['Category', 'Amount']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 1 },
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 50, halign: 'right' }
      }
    });

    // Add detailed records
    const detailsStartY = (doc as any).lastAutoTable.finalY + 8;
    doc.setFontSize(11);
    doc.text('Detailed Records', margin, detailsStartY);

    let columns: string[];
    let tableData: any[];

    switch (reportType) {
      case 'payroll':
        columns = ['Period', 'Employee', 'Pay Period', 'Pay Date', 'Gross Pay', 'Net Pay', 'Status'];
        tableData = reportData.map(entry => {
          const employee = employees.find(e => e.id === entry.employeeId);
          const payDate = entry.payDate ? parseLocalDateForDisplay(entry.payDate).toLocaleDateString() : 'N/A';
          return [
            getFormattedPeriod(entry),
            `${employee?.firstName} ${employee?.lastName}`,
            `${parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} - ${parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}`,
            payDate,
            formatCurrency(entry.grossPay),
            formatCurrency(entry.netPay),
            entry.status.charAt(0).toUpperCase() + entry.status.slice(1)
          ];
        });
        break;
      case 'deductions':
        columns = ['Period', 'Employee', 'Pay Date', 'Tax', 'Insurance', 'Retirement', 'Total'];
        tableData = reportData.map(entry => {
          const employee = employees.find(e => e.id === entry.employeeId);
          const payDate = entry.payDate ? parseLocalDateForDisplay(entry.payDate).toLocaleDateString() : 'N/A';
          return [
            getFormattedPeriod(entry),
            `${employee?.firstName} ${employee?.lastName}`,
            payDate,
            formatCurrency(entry.deductions.tax),
            formatCurrency(entry.deductions.insurance),
            formatCurrency(entry.deductions.retirement),
            formatCurrency(
              entry.deductions.tax +
              entry.deductions.insurance +
              entry.deductions.retirement
            )
          ];
        });
        break;
    }

    autoTable(doc, {
      startY: detailsStartY + 5,
      head: [columns],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 1 },
      margin: { left: margin, right: margin }
    });
  }

  // Save the PDF
  doc.save(`${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`);
};